import {useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {useIntl} from 'react-intl';
import {Lock, MicOff, MonitorUp, RefreshCw, VideoOff} from 'lucide-react';
import {useAppDispatch} from '../../../store/store';
import {groupCallMessages as gm} from '../../groupCalls/messages';
import {MinimizeButton, StageChip, StageControl, StageEndButton, StatusPill} from '../components/controls';
import {useElapsed} from '../hooks/useElapsed';
import type {MatrixCallLeg} from '../model/types';
import type {ControlSpec} from '../group/useGroupControls';
import {groupSurfaceMessages as gs} from '../group/groupMessages';
import {FeedVideo, LocalAvatar, PeerAvatar} from './MatrixMedia';
import {matrixActions} from './matrixActions';
import {hasLiveVideo, type MatrixCallMedia} from './useMatrixCallMedia';
import type {MatrixControls} from './useMatrixControls';
import {matrixSurfaceMessages as m} from './matrixMessages';

const Dock = ({specs}: {specs: readonly (ControlSpec | undefined)[]}) => (
    <>
        {specs.filter((spec): spec is ControlSpec => spec !== undefined).map(spec => (
            <StageControl
                key={spec.id}
                icon={spec.icon}
                label={spec.label}
                caption={spec.caption}
                tone={spec.tone}
                alert={spec.alert}
                pressed={spec.pressed}
                hasPopup={spec.hasPopup}
                disabled={spec.disabled}
                testId={spec.testId}
                onClick={spec.onClick}
            />
        ))}
    </>
);

const NameChip = ({name, muted}: {name: string; muted?: boolean}) => {
    const intl = useIntl();
    return (
        <span className="absolute bottom-4 left-4 inline-flex h-7 items-center gap-1.5 rounded-lg bg-[var(--phq-call-chip-scrim)] px-2.5 text-[13px] font-semibold">
            {name}
            {muted && (
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-[var(--phq-call-danger)]">
                    <MicOff size={12} aria-label={intl.formatMessage(gm.tileMuted)}/>
                </span>
            )}
        </span>
    );
};

const CameraOffLabel = () => {
    const intl = useIntl();
    return (
        <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[var(--phq-call-ink-muted)]">
            <VideoOff size={14} aria-hidden="true"/>
            {intl.formatMessage(gs.cameraOff)}
        </span>
    );
};

interface MatrixStageProps {
    readonly leg: MatrixCallLeg;
    readonly media: MatrixCallMedia;
    readonly controls: MatrixControls;
    readonly onMinimize: () => void;
}

export const MatrixStage = ({leg, media, controls, onMinimize}: MatrixStageProps) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const timer = useElapsed(leg.connectedAt);
    const region = useRef<HTMLElement>(null);
    const name = leg.identity.name ?? '';
    const remote = media.feeds.remoteScreen ?? media.feeds.remoteUsermedia;
    const remoteLive = !!media.feeds.remoteScreen || hasLiveVideo(media.feeds.remoteUsermedia);
    const localLive = hasLiveVideo(media.feeds.localUsermedia);
    const sharing = !!media.feeds.localScreen;
    const video = leg.kind === 'video' || remoteLive || localLive;
    const remoteMuted = media.feeds.remoteUsermedia?.isAudioMuted() ?? false;

    useEffect(() => {
        region.current?.focus();
    }, []);

    const status = leg.network === 'reconnecting'
        ? <StatusPill tone="reconnecting" icon={<RefreshCw size={16} className="animate-spin" aria-hidden="true"/>}>{intl.formatMessage(gs.pillReconnecting)}</StatusPill>
        : sharing
            ? <StatusPill tone="sharing" icon={<MonitorUp size={16} aria-hidden="true"/>}>{intl.formatMessage(gs.sharingYou)}</StatusPill>
            : leg.muted
                ? <StatusPill tone="muted" icon={<MicOff size={16} className="text-[var(--phq-call-danger-ink)]" aria-hidden="true"/>}>{intl.formatMessage(gs.noteMuted)}</StatusPill>
                : (
                    <StageChip icon={<Lock size={14} aria-hidden="true"/>}>
                        {timer
                            ? intl.formatMessage(video ? m.chipVideoTimer : m.chipVoiceTimer, {timer})
                            : intl.formatMessage(video ? m.chipVideo : m.chipVoice)}
                    </StageChip>
                );

    const subline = leg.heldByPeer
        ? intl.formatMessage(m.heldByPeer, {name})
        : leg.held && timer
            ? intl.formatMessage(m.onHold, {timer})
            : timer;

    return createPortal(
        <section
            ref={region}
            tabIndex={-1}
            aria-label={intl.formatMessage(m.callRegion, {name})}
            data-testid="one-to-one-call-surface"
            className="phq-call-ui phq-call-enter fixed bottom-0 left-14 right-0 top-0 z-[55] flex flex-col bg-[var(--phq-call-canvas)] [background-image:var(--phq-call-stage-gradient)] px-4 pb-5 pt-[60px] outline-none"
        >
            <div className="absolute left-4 top-4">{status}</div>
            <div className="absolute right-4 top-3">
                <MinimizeButton label={intl.formatMessage(gs.minimize)} testId="one-to-one-minimize" onClick={onMinimize}/>
            </div>
            {video ? (
                <div className="relative mb-4 min-h-0 flex-1">
                    <div className="relative flex h-full items-center justify-center overflow-hidden rounded-2xl bg-[var(--phq-call-surface)]">
                        {remote && remoteLive ? (
                            <FeedVideo feed={remote} contain={!!media.feeds.remoteScreen} testId="one-to-one-remote-video"/>
                        ) : (
                            <div className="flex flex-col items-center gap-3.5">
                                <PeerAvatar leg={leg} size={128}/>
                                <CameraOffLabel/>
                            </div>
                        )}
                        <NameChip name={media.feeds.remoteScreen ? intl.formatMessage(gm.tileScreen, {name}) : name} muted={remoteMuted}/>
                    </div>
                    <div className="absolute bottom-6 right-6 flex h-[132px] w-[200px] items-center justify-center overflow-hidden rounded-2xl bg-[var(--phq-call-surface-muted)] shadow-[inset_0_0_0_1px_var(--phq-call-line-strong)]">
                        {media.feeds.localUsermedia && localLive ? (
                            <FeedVideo feed={media.feeds.localUsermedia} mirror testId="one-to-one-local-video"/>
                        ) : (
                            <div className="flex flex-col items-center gap-2">
                                <LocalAvatar size={48}/>
                                <CameraOffLabel/>
                            </div>
                        )}
                        <span className="absolute bottom-2 left-2 inline-flex h-6 items-center rounded-lg bg-[var(--phq-call-chip-scrim)] px-2 text-xs font-semibold">{intl.formatMessage(gs.you)}</span>
                    </div>
                </div>
            ) : (
                <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-center">
                    <PeerAvatar leg={leg} size={128}/>
                    <h1 className="mt-3 text-[30px] font-semibold leading-[1.2]">{name}</h1>
                    <p className="text-lg font-medium tabular-nums text-[var(--phq-call-ink-muted)]">{subline}</p>
                </div>
            )}
            <div className="flex items-start justify-center gap-4">
                {video ? (
                    <Dock specs={[controls.microphone, controls.camera, controls.share, controls.hold, controls.keypad, controls.addCall, controls.devices]}/>
                ) : (
                    <Dock specs={[controls.microphone, controls.videoCta, controls.share, controls.keypad, controls.hold, controls.addCall, controls.devices]}/>
                )}
                <span className="w-4 shrink-0"/>
                <StageEndButton label={intl.formatMessage(m.endCall)} testId="one-to-one-hangup" onClick={() => matrixActions.end(leg.matrixCallId, dispatch)}/>
            </div>
        </section>,
        document.body,
    );
};
